<script setup>
import { reactive, computed, ref, watch, getCurrentInstance, onMounted } from 'vue'
import { useForm, router } from '@inertiajs/vue3';
import { watchDebounced } from '@vueuse/core';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
// GlobalLoadingOverlay (AdminMainLayout) renders the useEnhancedLoading page
// state; the old useLoading composable isn't displayed by any component here.
const { showPageLoading: showLoading, hidePageLoading: hideLoading } = useEnhancedLoading();

const props = defineProps({
    title: String,
    invoices: Object,
    clients: Array,
    coins: Object,
    filters: Object,
    companies: Array,
    bankAccounts: Array,
    pdfExportLimit: Number,
});

const links = computed(() => {
    const _links = props.invoices.links;
    _links.shift();
    _links.pop();
    return _links;
});

// Query params come back as strings; Calendar needs Date objects and the
// client Select expects a numeric id, otherwise the inputs show up empty
// after the filtered reload.
const parseDateRange = (value) => {
    if (!Array.isArray(value) || value.length === 0) return null;
    const dates = value.filter(Boolean).map(v => new Date(v));
    return dates.length ? dates : null;
};

const filters = reactive({
    search: props.filters?.search || null,
    client: props.filters?.client ? Number(props.filters.client) : null,
    status: props.filters?.status || null,
    created_at: parseDateRange(props.filters?.created_at),
});


watchDebounced(
    filters,
    (n, o) => {
        // Range started but end date not picked yet — wait for it
        if (Array.isArray(filters.created_at) && filters.created_at[0] && !filters.created_at[1]) {
            return;
        }
        get();
    },
    {
        debounce: 400,
        deep: true,
    }
);

// Calendar closed with only the start date picked — filter that single day
const onDateFilterHide = () => {
    const range = filters.created_at;
    if (Array.isArray(range) && range[0] && !range[1]) {
        filters.created_at = [range[0], range[0]];
    }
};

const get = async () => {
    router.get(
        proxy.route ? proxy.route('invoices.index') : '/admin/invoices',
        {
            filter: filters,
        },
        {
            preserveState: false,
            preserveScroll: true,
            only: ['invoices', 'filters'],
            onStart: () => {
                showLoading('Loading invoices...');
            },
            onFinish: () => {
                hideLoading();
            },
            onError: () => {
                hideLoading();
            }
        }
    );
};

// Status options
const statusOptions = [
    { label: 'Aguardando', value: 'WAITING' },
    { label: 'Produção do artigo', value: 'PRODUCTION' },
    { label: 'Enviado para vendedor', value: 'SUBMITTED' },
    { label: 'Aguardando publicação', value: 'PROCESSING' },
    { label: 'Publicado', value: 'PUBLISHED' },
    { label: 'Invoice', value: 'INVOICE' },
    { label: 'Finalizado', value: 'COMPLETED' },
];

// Get invoice status color
const getInvoiceStatusColor = (invoice) => {
    const statusColors = {
        'WAITING': 'bg-yellow-500',
        'PRODUCTION': 'bg-blue-500',
        'SUBMITTED': 'bg-purple-500',
        'PROCESSING': 'bg-indigo-500',
        'PUBLISHED': 'bg-green-500',
        'INVOICE': 'bg-orange-500',
        'COMPLETED': 'bg-gray-500'
    };
    return statusColors[invoice.status] || 'bg-gray-500';
};

const getPrimaryOrderId = (invoice) => {
    if (!invoice?.orders?.length) {
        console.warn('No orders found for invoice:', invoice?.id);
        return null;
    }
    return invoice.orders[0]?.id || null;
};

const navigateToInvoice = (invoice) => {
    const orderId = getPrimaryOrderId(invoice);
    
    if (!orderId) {
        console.error('Cannot navigate: No order ID found for invoice', invoice?.id);
        return;
    }
    
    try {
        router.visit(proxy.route('orders.invoice.show', { order: orderId }), {
            onStart: () => showLoading('Loading invoice...'),
            onFinish: () => hideLoading()
        });
    } catch (e) { 
        console.error('Navigation error:', e); 
    }
};

// Export dialog
const exportDialogVisible = ref(false);

const exportForm = reactive({
    type: 'full',
    company: null,
    bank_account: null,
    paid: null,
    created_at: null,
});

const exportTypeOptions = [
    { label: $t('Complete'), value: 'full' },
    { label: $t('Summary'), value: 'summary' },
];

const exportPaidOptions = [
    { label: $t('Paid'), value: '1' },
    { label: $t('Unpaid'), value: '0' },
];

const exportTypeDescription = computed(() => {
    return exportForm.type === 'summary'
        ? $t('Exports totals grouped by month, status and currency')
        : $t('Exports two sheets: invoices and payments (orders)');
});

// Bulk PDF export (uses the filters currently applied on the screen)
const canExportPdf = computed(() => {
    const limit = props.pdfExportLimit || 10;
    return props.invoices.total > 0 && props.invoices.total <= limit;
});

const pdfExportTooltip = computed(() => {
    const limit = props.pdfExportLimit || 10;

    if (props.invoices.total === 0) {
        return $t('No invoices found');
    }

    if (props.invoices.total > limit) {
        return $t('Maximum of :limit invoices per PDF export, refine your filters').replace(':limit', limit);
    }

    return $t('Exports the filtered invoices in a single PDF (maximum of :limit)').replace(':limit', limit);
});

const doExportPdf = () => {
    const base = proxy.route ? proxy.route('invoices.export-pdf') : '/admin/invoices/export-pdf';
    window.location.href = `${base}${window.location.search}`;
};

const doExport = () => {
    const params = new URLSearchParams();
    params.append('type', exportForm.type);

    if (exportForm.company) {
        params.append('filter[company]', exportForm.company);
    }

    if (exportForm.bank_account) {
        params.append('filter[bank_account]', exportForm.bank_account);
    }

    if (exportForm.paid !== null && exportForm.paid !== undefined) {
        params.append('filter[paid]', exportForm.paid);
    }

    if (Array.isArray(exportForm.created_at) && exportForm.created_at[0]) {
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const start = exportForm.created_at[0];
        const end = exportForm.created_at[1] || start;
        params.append('filter[created_at][0]', fmt(start));
        params.append('filter[created_at][1]', fmt(end));
    }

    const base = proxy.route ? proxy.route('invoices.export') : '/admin/invoices/export';
    window.location.href = `${base}?${params.toString()}`;
    exportDialogVisible.value = false;
};

onMounted(() => {
    // Initialize if needed
});
</script>

<template>
    <!-- Main Content -->
    <div class="grid">
        <!-- Search Filters Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex items-center justify-between p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <i class="pi pi-search text-2xl text-surface-500" />
                        <div>
                            <h2 class="text-xl font-bold">{{ $t('Search invoices') }}</h2>
                            <span class="text-sm text-surface-500">{{ $t('Choose filters to start') }}</span>
                        </div>
                    </div>
                </div>
            </template>

            <template #content>
                <div class="space-y-4">
                    <!-- Mobile: Stacked layout -->
                    <div class="block lg:hidden space-y-4">
                        <!-- Search by number -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Find by number:') }}
                            </label>
                            <IconField>
                                <InputIcon class="pi pi-search" />
                                <InputText
                                    v-model="filters.search"
                                    :placeholder="$t('Search')"
                                    class="w-full"
                                />
                            </IconField>
                        </div>

                        <!-- Filter by client -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by client') }}
                            </label>
                            <Select
                                v-model="filters.client"
                                :options="clients.map(client => ({ name: client.name, value: client.id }))"
                                optionLabel="name"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full"
                                showClear
                            />
                        </div>

                        <!-- Filter by status -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by status') }}
                            </label>
                            <Select
                                v-model="filters.status"
                                :options="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full"
                                showClear
                            />
                        </div>

                        <!-- Date filter -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Date') }}
                            </label>
                            <Calendar
                                v-model="filters.created_at"
                                selectionMode="range"
                                :manualInput="false"
                                showIcon
                                class="w-full"
                                dateFormat="dd/mm/yy"
                                showClear
                                @hide="onDateFilterHide"
                            />
                        </div>
                    </div>

                    <!-- Desktop: Grid layout -->
                    <div class="hidden lg:grid lg:grid-cols-4 gap-4">
                        <!-- Search by number -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Find by number:') }}
                            </label>
                            <IconField>
                                <InputIcon class="pi pi-search" />
                                <InputText
                                    v-model="filters.search"
                                    :placeholder="$t('Search')"
                                    class="w-full"
                                />
                            </IconField>
                        </div>

                        <!-- Filter by client -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by client') }}
                            </label>
                            <Select
                                v-model="filters.client"
                                :options="clients.map(client => ({ name: client.name, value: client.id }))"
                                optionLabel="name"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full"
                                showClear
                            />
                        </div>

                        <!-- Filter by status -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by status') }}
                            </label>
                            <Select
                                v-model="filters.status"
                                :options="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full"
                                showClear
                            />
                        </div>

                        <!-- Date filter -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Date') }}
                            </label>
                            <Calendar
                                v-model="filters.created_at"
                                selectionMode="range"
                                :manualInput="false"
                                showIcon
                                class="w-full"
                                dateFormat="dd/mm/yy"
                                showClear
                                @hide="onDateFilterHide"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Results Section -->
        <div class="flex flex-col">
            <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 gap-4">
                <div class="flex-1">
                    <h2 class="text-lg lg:text-xl font-semibold text-surface-800">
                        {{ $t('Showing results:') }} {{ invoices.total }} {{ $t('invoices found') }}
                    </h2>
                </div>
                <div class="flex flex-col lg:flex-row gap-2 w-full lg:w-auto">
                    <span v-tooltip.bottom="pdfExportTooltip" class="w-full lg:w-auto">
                        <Button
                            :label="`${$t('Export PDF')} (${invoices.total})`"
                            icon="pi pi-file-pdf"
                            outlined
                            severity="danger"
                            class="w-full"
                            :disabled="!canExportPdf"
                            @click="doExportPdf"
                        />
                    </span>
                    <Button
                        :label="$t('Export')"
                        icon="pi pi-download"
                        outlined
                        class="w-full lg:w-auto"
                        @click="exportDialogVisible = true"
                    />
                </div>
            </div>

            <!-- Mobile: Card View -->
            <div class="block lg:hidden">
                <div class="space-y-3">
                    <Card v-for="invoice in invoices.data" :key="invoice.id" class="p-4">
                        <template #content>
                            <div class="flex justify-between items-start mb-3">
                                <div>
                                    <h3 class="font-medium text-surface-900">#{{ invoice.number }}</h3>
                                    <p class="text-sm text-surface-600">{{ invoice.client?.name || "-" }}</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <Tag 
                                        :value="invoice.formatted_status || '-'"
                                        :class="getInvoiceStatusColor(invoice)"
                                        class="text-xs"
                                    />
                                    <Button
                                        icon="pi pi-pencil"
                                        text
                                        rounded
                                        size="small"
                                        @click="() => navigateToInvoice(invoice)"
                                    />
                                </div>
                            </div>
                            <div class="text-sm text-surface-600">
                                <span class="font-medium text-surface-700">{{ $t('Created at') }}:</span>
                                {{ new Date(invoice.created_at).toLocaleDateString() }}
                            </div>
                        </template>
                    </Card>
                </div>
                
                
                <!-- Mobile Pagination -->
                <div class="flex justify-between items-center mt-4 gap-2">
                    <Button 
                        :label="$t('Previous')" 
                        icon="pi pi-chevron-left" 
                        severity="secondary" 
                        size="small"
                        :disabled="!invoices.prev_page_url"
                        @click="() => {
                            if (invoices.prev_page_url) {
                                router.visit(invoices.prev_page_url, {
                                    onStart: () => showLoading('Loading previous page...'),
                                    onFinish: () => hideLoading()
                                });
                            }
                        }"
                    />
                    <span class="text-sm text-surface-600">
                        {{ ((invoices.current_page || 1) - 1) * (invoices.per_page || 15) + 1 }} - 
                        {{ Math.min((invoices.current_page || 1) * (invoices.per_page || 15), invoices.total || 0) }} 
                        {{ $t('of') }} {{ invoices.total || 0 }}
                    </span>
                    <Button 
                        :label="$t('Next')" 
                        icon="pi pi-chevron-right" 
                        iconPos="right"
                        severity="secondary" 
                        size="small"
                        :disabled="!invoices.next_page_url"
                        @click="() => {
                            if (invoices.next_page_url) {
                                router.visit(invoices.next_page_url, {
                                    onStart: () => showLoading('Loading next page...'),
                                    onFinish: () => hideLoading()
                                });
                            }
                        }"
                    />
                </div>
            </div>

            <!-- Desktop: Table View -->
            <div class="hidden lg:block">
                <DataTable
                    :value="invoices.data"
                    :paginator="false"
                    scrollable
                    scrollHeight="600px"
                    class="invoices-table"
                >
                    <Column field="number" :header="$t('Number')" sortable style="min-width: 120px; white-space: nowrap">
                        <template #body="slotProps">
                            #{{ slotProps.data.number }}
                        </template>
                    </Column>

                    <Column :header="$t('Client')" style="min-width: 160px">
                        <template #body="slotProps">
                            {{ slotProps.data.client?.name || "-" }}
                        </template>
                    </Column>

                    <Column :header="$t('Status')" style="min-width: 140px; white-space: nowrap">
                        <template #body="slotProps">
                            <Tag
                                :value="slotProps.data.formatted_status || '-'"
                                :class="getInvoiceStatusColor(slotProps.data)"
                            />
                        </template>
                    </Column>

                    <Column :header="$t('Created at')" sortable field="created_at" style="min-width: 170px">
                        <template #body="slotProps">
                            {{ new Date(slotProps.data.created_at).toLocaleString() }}
                        </template>
                    </Column>

                    <Column :header="$t('Actions')" headerClass="text-right" bodyClass="text-right" style="min-width: 100px; white-space: nowrap">
                        <template #body="slotProps">
                            <Button
                                icon="pi pi-pencil"
                                text
                                rounded
                                @click="() => navigateToInvoice(slotProps.data)"
                            />
                        </template>
                    </Column>

                    <template #footer>
                        <Paginator
                            v-if="invoices?.per_page"
                            :rows="invoices.per_page"
                            :totalRecords="invoices.total || 0"
                            :first="((invoices.current_page || 1) - 1) * (invoices.per_page || 15)"
                            @page="(event) => {
                                try {
                                    const link = invoices.links?.[event.page + 1];
                                    if (link?.url) {
                                        router.visit(link.url, {
                                            onStart: () => showLoading('Loading page...'),
                                            onFinish: () => hideLoading()
                                        });
                                    }
                                } catch (e) { console.error(e); }
                            }"
                        />
                    </template>
                </DataTable>
            </div>
        </div>

        <!-- Export Dialog -->
        <Dialog
            v-model:visible="exportDialogVisible"
            modal
            :header="$t('Export invoices')"
            :style="{ width: '34rem' }"
            :breakpoints="{ '640px': '95vw' }"
        >
            <div class="space-y-4">
                <!-- Export type -->
                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">
                        {{ $t('Export type') }}
                    </label>
                    <SelectButton
                        v-model="exportForm.type"
                        :options="exportTypeOptions"
                        optionLabel="label"
                        optionValue="value"
                        :allowEmpty="false"
                    />
                    <small class="block mt-1 text-surface-500">
                        {{ exportTypeDescription }}
                    </small>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <!-- Company -->
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Company') }}
                        </label>
                        <Select
                            v-model="exportForm.company"
                            :options="(companies || []).map(company => ({ name: company.name, value: company.id }))"
                            optionLabel="name"
                            optionValue="value"
                            :placeholder="$t('All')"
                            class="w-full"
                            showClear
                        />
                    </div>

                    <!-- Bank account -->
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Bank account') }}
                        </label>
                        <Select
                            v-model="exportForm.bank_account"
                            :options="(bankAccounts || []).map(account => ({ name: account.name, value: account.id }))"
                            optionLabel="name"
                            optionValue="value"
                            :placeholder="$t('All')"
                            class="w-full"
                            showClear
                        />
                    </div>

                    <!-- Paid status -->
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Payment Status') }}
                        </label>
                        <Select
                            v-model="exportForm.paid"
                            :options="exportPaidOptions"
                            optionLabel="label"
                            optionValue="value"
                            :placeholder="$t('All')"
                            class="w-full"
                            showClear
                        />
                    </div>

                    <!-- Period -->
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Period') }}
                        </label>
                        <Calendar
                            v-model="exportForm.created_at"
                            selectionMode="range"
                            :manualInput="false"
                            showIcon
                            class="w-full"
                            dateFormat="dd/mm/yy"
                            showClear
                        />
                    </div>
                </div>

                <small class="block text-surface-500">
                    <i class="pi pi-info-circle mr-1" />
                    {{ $t('Leave filters empty to export all invoices at once') }}
                </small>
            </div>

            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    text
                    severity="secondary"
                    @click="exportDialogVisible = false"
                />
                <Button
                    :label="$t('Export')"
                    icon="pi pi-download"
                    @click="doExport"
                />
            </template>
        </Dialog>
    </div>
</template>

<style scoped>
/* Invoices table: header titles must always stay on a single line */
.invoices-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.invoices-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}

.p-tag {
    color: white;
    font-weight: bold;
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
}

:deep(.p-select) {
    height: 2.67rem;
    display: flex;
    align-items: center;
}

:deep(.p-select-label) {
    display: flex;
    align-items: center;
}
</style>